import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  CancelGoodsReceiptRequest,
  CreateGoodsReceiptRequest,
  GoodsReceiptDetail,
  GoodsReceiptListItem,
  GoodsReceiptListQuery,
  InspectGoodsReceiptLineRequest,
  ReceivableOrderLine,
  ReceivableOrderOption,
} from './goodsReceiptTypes';

/**
 * Serializes the base list query.
 *
 * Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
 * the reader saying "none of these", which matches no row. A truthiness check would drop it and
 * the server would read the axis as unmentioned, which is the opposite result.
 */
function base(query: ListQuery, params: URLSearchParams): URLSearchParams {
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  return params;
}

function receiptQuery(query: GoodsReceiptListQuery): string {
  const params = base(query, new URLSearchParams());

  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.supplier !== undefined) params.set('supplier', query.supplier);
  if (query.facility !== undefined) params.set('facility', query.facility);
  if (query.purchaseOrderId) params.set('purchaseOrderId', query.purchaseOrderId);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * Supplier deliveries.
 *
 * <b>There is no `update`.</b> A receipt records what physically arrived, and the fields worth
 * changing afterwards are the ones a correction must not touch. What looks like an edit is an
 * INSPECTION (a verdict on a line) or a CANCELLATION (it was keyed against the wrong order), and
 * each says which of the two occurred.
 */
export const goodsReceiptsApi = {
  list: (query: GoodsReceiptListQuery, signal?: AbortSignal) =>
    api<PagedResult<GoodsReceiptListItem>>(`/api/goods-receipts${receiptQuery(query)}`, { signal }),

  /**
   * One delivery and its lines.
   *
   * `includeDeleted` is what the register's Show deleted sets — without it a struck receipt is
   * returned by no read, and the restore endpoint has no way to be reached.
   */
  get: (id: string, includeDeleted = false, signal?: AbortSignal) =>
    api<GoodsReceiptDetail>(
      `/api/goods-receipts/${id}${includeDeleted ? '?includeDeleted=true' : ''}`,
      { signal },
    ),

  /**
   * The orders a delivery may be recorded against.
   *
   * Carries `goods-receipts.create`, not `read` — the feed discloses what is on order and how
   * much is still outstanding, which is a wider fact than the list of past deliveries.
   */
  receivableOrders: (signal?: AbortSignal) =>
    api<ReceivableOrderOption[]>('/api/goods-receipts/receivable-orders', { signal }),

  receivableLines: (purchaseOrderId: string, signal?: AbortSignal) =>
    api<ReceivableOrderLine[]>(
      `/api/goods-receipts/receivable-orders/${purchaseOrderId}/lines`,
      { signal },
    ),

  create: (body: CreateGoodsReceiptRequest) =>
    api<GoodsReceiptDetail>('/api/goods-receipts', { method: 'POST', body }),

  /** Record ONE line's verdict. A delivery is inspected item by item. */
  inspectLine: (id: string, lineId: string, body: InspectGoodsReceiptLineRequest) =>
    api<GoodsReceiptDetail>(`/api/goods-receipts/${id}/lines/${lineId}/inspection`, {
      method: 'PUT',
      body,
    }),

  /** Turn the accepted quantities into stock. Refused until every line is inspected. */
  register: (id: string) =>
    api<GoodsReceiptDetail>(`/api/goods-receipts/${id}/register`, { method: 'POST' }),

  cancel: (id: string, body: CancelGoodsReceiptRequest) =>
    api<GoodsReceiptDetail>(`/api/goods-receipts/${id}/cancel`, { method: 'POST', body }),

  deactivate: (id: string) => api<void>(`/api/goods-receipts/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/goods-receipts/${id}/restore`, { method: 'POST' }),
};
